import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold transition-spring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "gradient-primary text-white shadow-primary hover:scale-105 hover:shadow-lg",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive-light shadow-sm hover:shadow-md",
        outline: "border-2 border-border bg-background hover:bg-accent hover:border-primary/30 hover:text-primary transition-spring",
        secondary: "gradient-success text-white shadow-success hover:scale-105 hover:shadow-lg",
        ghost: "hover:bg-accent hover:text-accent-foreground transition-fast",
        link: "text-primary underline-offset-4 hover:underline transition-fast",
        hero: "gradient-hero text-white shadow-primary hover:scale-105 hover:shadow-xl font-bold",
        medical: "bg-card text-card-foreground border border-border/50 hover:shadow-card-hover hover:border-primary/30 transition-spring",
        warning: "gradient-warning text-white shadow-warning hover:scale-105 hover:shadow-lg",
        professional: "bg-white text-primary border-2 border-primary hover:bg-primary hover:text-white shadow-sm hover:shadow-primary transition-spring",
      },
      size: {
        default: "h-11 px-6 py-3 text-sm",
        sm: "h-9 rounded-lg px-4 text-sm",
        lg: "h-12 rounded-xl px-8 text-base font-semibold",
        xl: "h-14 rounded-xl px-10 text-lg font-bold",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
